import { format, parse, set } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Recital Manager Timezone Utilities
 *
 * These utilities handle timezone conversions for the recital manager.
 * All dates are stored as UTC in the database and converted to studio timezone for display.
 */

/**
 * Format a UTC date/time for display in the studio's timezone
 *
 * @param utcDate - UTC date string or Date object from backend
 * @param studioTimezone - Studio timezone (e.g., "America/New_York")
 * @param formatString - date-fns format string (default: "PPP" = "Apr 29, 2023")
 * @returns Formatted date string in studio timezone
 *
 * @example
 * formatRecitalDate("2024-03-15T19:00:00Z", "America/Los_Angeles", "PPP")
 * // => "March 15, 2024"
 */
export function formatRecitalDate(
  utcDate: string | Date,
  studioTimezone: string,
  formatString: string = 'PPP',
): string {
  if (!utcDate) return '';
  try {
    return formatInTimeZone(utcDate, studioTimezone, formatString);
  } catch (error) {
    console.error('Error formatting recital date:', error);
    return 'Invalid date';
  }
}

/**
 * Format a UTC date/time for display as time only in the studio's timezone
 *
 * @param utcDate - UTC date string or Date object from backend
 * @param studioTimezone - Studio timezone (e.g., "America/New_York")
 * @param formatString - date-fns format string (default: "h:mm a" = "7:00 PM")
 * @returns Formatted time string in studio timezone
 *
 * @example
 * formatRecitalTime("2024-03-15T19:00:00Z", "America/Los_Angeles")
 * // => "12:00 PM" (if UTC 7:00 PM = PST 12:00 PM)
 */
export function formatRecitalTime(
  utcDate: string | Date,
  studioTimezone: string,
  formatString: string = 'h:mm a',
): string {
  if (!utcDate) return '';
  try {
    return formatInTimeZone(utcDate, studioTimezone, formatString);
  } catch (error) {
    console.error('Error formatting recital time:', error);
    return 'Invalid time';
  }
}

/**
 * Convert a datetime-local input value to UTC Date for backend storage
 *
 * This is used when a user selects a date/time in a datetime-local input.
 * The input is in studio timezone and needs to be converted to UTC.
 *
 * @param localDateString - datetime-local value (e.g., "2024-03-15T19:00")
 * @param studioTimezone - Studio timezone (e.g., "America/New_York")
 * @returns UTC Date object
 *
 * @example
 * convertLocalToUTC("2024-03-15T19:00", "America/New_York")
 * // => Date object representing UTC time for 7:00 PM ET
 */
export function convertLocalToUTC(
  localDateString: string,
  studioTimezone: string,
): Date {
  if (!localDateString) return new Date();
  try {
    // datetime-local is minute-precision. Normalize explicitly so the UTC
    // instant is always at :00.000 regardless of parse reference quirks.
    const localDate = set(
      parse(localDateString, "yyyy-MM-dd'T'HH:mm", new Date(0)),
      { seconds: 0, milliseconds: 0 },
    );
    // Convert from studio timezone to UTC
    return fromZonedTime(localDate, studioTimezone);
  } catch (error) {
    console.error('Error converting local to UTC:', error);
    return new Date();
  }
}

/**
 * Convert a UTC date to datetime-local input format in studio timezone
 *
 * This is used to populate datetime-local inputs with existing data.
 * The UTC date is converted to studio timezone and formatted for the input.
 *
 * @param utcDate - UTC date string or Date object from backend
 * @param studioTimezone - Studio timezone (e.g., "America/New_York")
 * @returns datetime-local formatted string (yyyy-MM-ddTHH:mm)
 *
 * @example
 * convertUTCToLocal("2024-03-15T23:00:00Z", "America/New_York")
 * // => "2024-03-15T19:00" (if UTC 11:00 PM = ET 7:00 PM)
 */
export function convertUTCToLocal(
  utcDate: string | Date,
  studioTimezone: string,
): string {
  if (!utcDate) return '';
  try {
    const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    const zonedDate = toZonedTime(date, studioTimezone);
    return format(zonedDate, "yyyy-MM-dd'T'HH:mm");
  } catch (error) {
    console.error('Error converting UTC to local:', error);
    return '';
  }
}

/**
 * Combine a show date with a time string (HH:mm format) in studio timezone
 *
 * Shows store date and time separately. This combines them for display.
 *
 * @param showDate - Show date (Date or string)
 * @param timeString - Time in "HH:mm" format (e.g., "19:00")
 * @param studioTimezone - Studio timezone
 * @returns Combined Date object in UTC
 *
 * @example
 * combineDateTime("2024-03-15", "19:00", "America/New_York")
 * // => Date object representing 7:00 PM ET on March 15, 2024 (in UTC)
 */
export function combineDateTime(
  showDate: string | Date,
  timeString: string,
  studioTimezone: string,
): Date {
  if (!showDate || !timeString) return new Date();
  try {
    const date = typeof showDate === 'string' ? new Date(showDate) : showDate;
    const [hours, minutes] = timeString.split(':').map(Number);

    // Convert to studio timezone first
    const zonedDate = toZonedTime(date, studioTimezone);

    // Set the time
    const combinedDate = set(zonedDate, {
      hours: hours || 0,
      minutes: minutes || 0,
      seconds: 0,
      milliseconds: 0,
    });

    // Convert back to UTC
    return fromZonedTime(combinedDate, studioTimezone);
  } catch (error) {
    console.error('Error combining date and time:', error);
    return new Date();
  }
}

/**
 * Format a combined show date and time for display
 *
 * @param showDate - Show date (Date or string)
 * @param timeString - Time in "HH:mm" format (e.g., "19:00")
 * @param studioTimezone - Studio timezone
 * @param formatString - date-fns format string (default: "PPP 'at' p")
 * @returns Formatted datetime string
 *
 * @example
 * formatShowDateTime("2024-03-15", "19:00", "America/New_York")
 * // => "March 15, 2024 at 7:00 PM"
 */
export function formatShowDateTime(
  showDate: string | Date,
  timeString: string,
  studioTimezone: string,
  formatString: string = "PPP 'at' p",
): string {
  if (!showDate || !timeString) return '';
  try {
    const combined = combineDateTime(showDate, timeString, studioTimezone);
    return formatInTimeZone(combined, studioTimezone, formatString);
  } catch (error) {
    console.error('Error formatting show datetime:', error);
    return 'Invalid datetime';
  }
}
